import React, { useState, useEffect } from "react";
import { env } from "../../helpers/GlobalStateProvider";
import { useModalState } from "../../helpers/ModalProvider";
import { getApiUrl } from "../../config";
import axios from "axios";
import { Flex, Input, Text } from "@chakra-ui/react";
import { Button } from "../../components/ui/button";
import { Field } from "../../components/ui/field";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "../../components/ui/dialog";
import { useAccount, useSignTypedData, useSwitchChain } from "wagmi";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { ethers } from "ethers";
import { LuArrowRight, LuChevronDown } from "react-icons/lu";
import { updatePrepaidKeyCredits } from "../../helpers/prepaidKeyManager";

// Prepaid credits cost $0.002 each — 500 credits per $1 USDC, matching
// CREDITS_PER_DOLLAR on the backend (which also advertises `creditsPerDollar`
// in its API responses). USDC has 6 decimals, so one credit is exactly 2000
// smallest units and the conversion stays integral with no rounding drift.
const CREDIT_PRICE_USD = 0.002;
const USDC_UNITS_PER_CREDIT = 2000;
// Backend rejects payments under $0.01 USDC.
const MIN_CREDITS = 5;
const BASE_CHAIN_ID = 8453;

const RefillPrepaidKeyModal: React.FC = () => {
  const {
    isRefillPrepaidModalVisible,
    setRefillPrepaidModalVisible,
    refillPrepaidKeyId,
    showPrepaidRefilledModal,
    showErrorMessage,
  } = useModalState();
  const { address, status, chainId } = useAccount();
  const isWalletConnected = status === 'connected';
  const { openConnectModal } = useConnectModal();
  const { signTypedDataAsync } = useSignTypedData();
  const { switchChainAsync } = useSwitchChain();

  const [credits, setCredits] = useState<number>(4000);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isRefillPrepaidModalVisible) {
      setCredits(4000);
      setIsSubmitting(false);
    }
  }, [isRefillPrepaidModalVisible]);

  const totalCost = (credits * CREDIT_PRICE_USD).toFixed(2);

  // Refill is an x402 (EIP-3009) payment flow:
  //   1. POST unpaid -> backend answers 402 with the payment requirements
  //   2. Sign a USDC transferWithAuthorization for the amount owed
  //   3. Re-POST with the signed authorization in the PAYMENT header
  // The backend broadcasts and pays gas, so the user only signs — no ETH needed.
  const handleRefill = async () => {
    if (!isWalletConnected || !address) {
      openConnectModal?.();
      return;
    }
    // refillPrepaidKeyId is nullable — bail before signing anything rather
    // than paying against an invalid key.
    if (!refillPrepaidKeyId) {
      showErrorMessage("No prepaid key selected. Close and try again.");
      return;
    }
    if (credits < MIN_CREDITS) {
      showErrorMessage(`Minimum refill is ${MIN_CREDITS} credits ($0.01 USDC).`);
      return;
    }
    // credits feeds an exact-unit conversion below; keep it a sane integer.
    if (!Number.isSafeInteger(credits)) {
      showErrorMessage("Please enter a valid number of credits.");
      return;
    }

    const url = `${getApiUrl(env)}/v3/prepaid/refill`;
    const body = { apiKey: refillPrepaidKeyId };

    try {
      setIsSubmitting(true);

      // 1. Ask for the challenge. Read the recipient/domain off the response
      // rather than hardcoding them, so backend changes don't need a frontend
      // deploy. validateStatus keeps axios from throwing on the expected 402.
      const challenge = await axios.post(url, body, { validateStatus: () => true });
      const x402 = challenge.data?.x402;
      if (challenge.status !== 402 || !x402) {
        throw new Error(
          challenge.data?.error || `Unexpected response from server (${challenge.status})`,
        );
      }

      // 2. Sign on whatever chain the challenge is domain-bound to (Base
      // today). Taking it from the response means a backend chain change
      // needs no frontend deploy; BASE_CHAIN_ID is only a fallback.
      const requiredChainId = Number(x402.eip712Domain?.chainId) || BASE_CHAIN_ID;
      if (chainId !== requiredChainId) {
        await switchChainAsync({ chainId: requiredChainId as typeof BASE_CHAIN_ID });
      }

      const nowSec = Math.floor(Date.now() / 1000);
      const authorization = {
        from: address,
        to: x402.payTo as `0x${string}`,
        // Stay in BigInt for the whole conversion — multiplying as a JS
        // number first could lose precision on large inputs.
        value: BigInt(credits) * BigInt(USDC_UNITS_PER_CREDIT),
        validAfter: BigInt(nowSec - 60), // backdated slightly for clock skew
        validBefore: BigInt(nowSec + 600), // 10 min to sign and settle
        nonce: ethers.hexlify(ethers.randomBytes(32)) as `0x${string}`,
      };

      const signature = await signTypedDataAsync({
        domain: {
          name: x402.eip712Domain.name,
          version: x402.eip712Domain.version,
          chainId: requiredChainId,
          verifyingContract: x402.eip712Domain.verifyingContract as `0x${string}`,
        },
        types: {
          TransferWithAuthorization: [
            { name: "from", type: "address" },
            { name: "to", type: "address" },
            { name: "value", type: "uint256" },
            { name: "validAfter", type: "uint256" },
            { name: "validBefore", type: "uint256" },
            { name: "nonce", type: "bytes32" },
          ],
        },
        primaryType: "TransferWithAuthorization",
        message: authorization,
      });

      // 3. The contract call takes the signature split into v/r/s.
      const { v, r, s } = ethers.Signature.from(signature);
      const paymentHeader = btoa(
        JSON.stringify({
          from: authorization.from,
          to: authorization.to,
          value: authorization.value.toString(),
          validAfter: authorization.validAfter.toString(),
          validBefore: authorization.validBefore.toString(),
          nonce: authorization.nonce,
          v,
          r,
          s,
        }),
      );

      const paid = await axios.post(url, body, {
        headers: { PAYMENT: paymentHeader },
        validateStatus: () => true,
      });
      if (paid.status !== 200) {
        throw new Error(paid.data?.error || `Payment failed (${paid.status})`);
      }

      // Credits come from the settled amount, so trust the server's number
      // rather than assuming we got exactly what we asked for.
      const creditsAdded: number = paid.data?.creditsAdded ?? 0;

      // Mirror into local (sessionStorage) state; also dispatches
      // "prepaidKeysUpdated" so the keys table refreshes.
      if (refillPrepaidKeyId) {
        updatePrepaidKeyCredits(refillPrepaidKeyId, creditsAdded);
      }

      setRefillPrepaidModalVisible(false);

      showPrepaidRefilledModal({
        apiKey: refillPrepaidKeyId || "—",
        credits: creditsAdded,
      });
    } catch (error: any) {
      if (process.env.NODE_ENV === 'development') console.error("Error refilling prepaid key:", error);
      const message = String(error?.shortMessage || error?.message || "");
      // Declining the signature or the network switch is a deliberate cancel,
      // not a failure worth alarming the user about.
      if (/user rejected|user denied|rejected the request/i.test(message)) return;
      showErrorMessage(
        message ? `Refill failed: ${message}` : "Failed to refill prepaid key. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const walletDisplay = isWalletConnected && address
    ? `${address.slice(0, 6)}...${address.slice(-4)}`
    : "No wallet connected";

  const keyDisplay = refillPrepaidKeyId
    ? `${refillPrepaidKeyId.slice(0, 12)}...${refillPrepaidKeyId.slice(-8)}`
    : "—";

  return (
    <DialogRoot
      lazyMount
      open={isRefillPrepaidModalVisible}
      onOpenChange={(e) => setRefillPrepaidModalVisible(e.open)}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Refill API key</DialogTitle>
          <DialogCloseTrigger />
        </DialogHeader>

        <DialogBody>
          <Flex direction="column" gap={5}>
            {/* API Key (readonly select-style display) */}
            <Field label="API Key">
              <Flex
                bg="#f4f4f5"
                borderRadius="lg"
                height="40px"
                px={3}
                justifyContent="space-between"
                alignItems="center"
              >
                <Text fontSize="sm" color="fg.muted" fontFamily="mono">
                  {keyDisplay}
                </Text>
                <LuChevronDown size={16} color="#a1a1aa" />
              </Flex>
            </Field>

            {/* Credits + Total Cost side-by-side */}
            <Flex direction="row" gap="6px">
              <Field label="Credits" flex={1}>
                <Input
                  type="number"
                  value={credits}
                  onChange={(e) => setCredits(Math.max(0, parseInt(e.target.value) || 0))}
                  placeholder="e.g. 4000"
                />
              </Field>

              <Field label="Total Cost" flex={1}>
                <Flex
                  alignItems="center"
                  borderWidth={1}
                  borderColor="border"
                  borderRadius="lg"
                  height="40px"
                  px={3}
                  gap={1}
                >
                  <Text fontSize="sm" color="fg.muted">$</Text>
                  <Text fontSize="sm" fontWeight="medium" flex={1}>
                    {totalCost}
                  </Text>
                  <Text fontSize="sm" color="fg.muted">USDC</Text>
                </Flex>
              </Field>
            </Flex>

            <Text fontSize="xs" color="fg.muted" mt={-3}>
              500 credits per $1 USDC on Base. Minimum {MIN_CREDITS} credits ($0.01).
              You'll sign a gasless payment authorization — no ETH required.
            </Text>

            {/* Pay From (Wallet) */}
            <Field label="Pay From (Wallet)">
              <Flex
                bg="#f4f4f5"
                borderRadius="lg"
                height="40px"
                px={3}
                justifyContent="space-between"
                alignItems="center"
                cursor="pointer"
                onClick={() => {
                  if (!isWalletConnected) openConnectModal?.();
                }}
              >
                <Text fontSize="sm">
                  {isWalletConnected ? `Connected Wallet (${walletDisplay})` : "Connect Wallet"}
                </Text>
                <LuChevronDown size={16} />
              </Flex>
            </Field>
          </Flex>
        </DialogBody>

        <DialogFooter>
          <Button
            variant="solid"
            colorPalette="gray"
            width="full"
            onClick={handleRefill}
            disabled={credits < MIN_CREDITS || isSubmitting}
          >
            {isSubmitting ? "Confirm in your wallet…" : "Refill Prepaid Key"}
            {!isSubmitting && <LuArrowRight />}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};

export default RefillPrepaidKeyModal;
